import { MapPin } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { Profile } from '@/types';
import { parseStringArray } from '../helpers';

interface ProfileMetaProps {
  profile: Profile;
  className?: string;
}

/**
 * The textual half of the profile: headline, bio, skill chips, and location. The
 * handle and the WINR / WINR-since meta now live on the hero preview card. All
 * fields are real `profiles` columns — nothing invented.
 */
export function ProfileMeta({ profile, className }: ProfileMetaProps) {
  const skills = parseStringArray(profile.skills);

  return (
    <section className={cn('mb-6 rounded-2xl border border-edge bg-white/[0.03] p-6', className)}>
      {profile.headline && <div className="text-base font-bold text-hi">{profile.headline}</div>}
      {profile.bio && <p className="mt-3 text-sm leading-relaxed text-mid">{profile.bio}</p>}

      {skills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {skills.map((skill) => (
            <Badge key={skill} tone="neutral">
              {skill}
            </Badge>
          ))}
        </div>
      )}

      {profile.location && (
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 border-t border-edge pt-4 text-xs text-lo">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={13} /> {profile.location}
          </span>
        </div>
      )}
    </section>
  );
}
